import { REJECTION_REASON_LABELS_PL } from "@anisync/types";
import { BotIcon, CaptionsIcon, MicIcon, PlayIcon, ThumbsDownIcon, Undo2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import { ApiError, type VersionOption, getEpisodeVersions, restoreVersion } from "../lib/api";
import { stagger } from "../lib/motion";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { LoadingNote, SLOW_SOURCES_LABEL } from "./ui/loading";
import { SkeletonRows } from "./ui/skeleton";

/**
 * Wybór wersji odcinka przed pobraniem.
 *
 * Powstało ze zgłoszenia z produkcji: odcinek pobrał się ze **sztucznym
 * lektorem**, bo kaskada szeregowała kandydatów wyłącznie po prędkości hosta —
 * a pole `audio` z `PlayerRef`, które od początku odróżniało lektor od napisów,
 * nie było czytane przez nikogo.
 *
 * Ranking naprawiono osobno i to on rozstrzyga domyślny wybór. To okno jest
 * odpowiedzią na drugą połowę problemu: **nie było jak zobaczyć, co się bierze**.
 * Ranking poprawia trafność, ale nie zastępuje decyzji — serwisy oznaczają rodzaj
 * tłumaczenia nierzetelnie, więc ostatnie słowo musi należeć do widza.
 */
export function VersionPicker({
  titleId,
  episodeNumber,
  open,
  onOpenChange,
  onPick,
}: {
  titleId: string;
  episodeNumber: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Wołane z wyborem — strona tytułu uruchamia pobranie i przechodzi dalej. */
  onPick: (option: VersionOption) => void;
}) {
  const [items, setItems] = useState<VersionOption[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  /** Podbijane po przywróceniu — lista pyta źródła od nowa. */
  const [reloadKey, setReloadKey] = useState(0);

  const restore = async (rejectionId: string) => {
    setRestoring(rejectionId);
    try {
      await restoreVersion(rejectionId);
      setReloadKey((k) => k + 1);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : "Nie udało się przywrócić kopii");
    } finally {
      setRestoring(null);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: `reloadKey` wymusza ponowne pytanie po przywróceniu
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setItems(null);
    setError(null);
    getEpisodeVersions(titleId, episodeNumber)
      .then((data) => {
        if (!cancelled) setItems(data.items);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setItems([]);
        setError(e instanceof ApiError ? e.message : "Nie udało się sprawdzić wersji");
      });
    return () => {
      cancelled = true;
    };
  }, [open, titleId, episodeNumber, reloadKey]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogTitle>Wersje odcinka {episodeNumber}</AlertDialogTitle>
        <AlertDialogDescription>
          {items === null
            ? "Zbieram kopie tego odcinka ze wszystkich włączonych źródeł."
            : "Kolejność jest rankingiem pobieralności. Napisy stoją przed lektorem — wybierz inaczej, jeśli chcesz."}
        </AlertDialogDescription>

        {error !== null && <p className="text-sm text-destructive">{error}</p>}

        {/* Kształt listy na czas pytania: z Shinden trwa to do pół minuty, a sam
            napis przez tyle czasu wyglądał jak okno, które się zawiesiło. */}
        {items === null && (
          <div className="flex flex-col gap-3">
            <LoadingNote label="Pytam źródła o kopie…" slowLabel={SLOW_SOURCES_LABEL} />
            <SkeletonRows count={4} />
          </div>
        )}

        {items !== null && items.length === 0 && error === null && (
          <p className="text-sm text-muted-foreground">
            Żadne włączone źródło nie ma tego odcinka. Sprawdź wskazania źródeł dla tego tytułu albo
            odśwież katalog.
          </p>
        )}

        {items !== null && items.length > 0 && (
          <ul className="flex max-h-80 flex-col gap-2 overflow-y-auto">
            {items.map((option, index) => {
              const copy = copyLabel(items, index);
              /**
               * Język napisów kopii bije język źródła. Shinden jest serwisem polskim,
               * ale wystawia też kopie z napisami angielskimi i francuskimi — plakietka
               * „PL” przy nich byłaby dokładnie tym kłamstwem, przez które powstała.
               */
              const lang = option.subsLang ?? option.language;
              const foreignSubs = option.subsLang !== null && option.subsLang !== "pl";
              return (
                <li
                  // Adres, nie para host+etykieta: trzy kopie na jednym hoście z tą samą
                  // etykietą grupy dawały Reactowi trzy identyczne klucze.
                  key={option.embedUrl}
                  style={stagger(index)}
                  className="enter-rise flex flex-wrap items-center gap-2 rounded-lg border border-border p-3 text-sm"
                >
                  <Badge variant="secondary">{option.sourceLabel}</Badge>

                  {/*
                  Rodzaj tłumaczenia jako pierwsza rzecz po źródle — to po niego
                  się tu przychodzi. Brak oznaczenia mówimy WPROST zamiast
                  domyślać się napisów: serwis, który nie deklaruje lektora,
                  nadal może go mieć.
                */}
                  {option.translation === "dub" ? (
                    <Badge variant="warning">
                      <MicIcon />
                      lektor
                    </Badge>
                  ) : option.translation === "subs" ? (
                    <Badge variant={foreignSubs ? "warning" : "success"}>
                      <CaptionsIcon />
                      {foreignSubs ? `napisy ${lang.toUpperCase()}` : "napisy"}
                    </Badge>
                  ) : (
                    <Badge variant="outline">nieoznaczone</Badge>
                  )}

                  <Badge variant={lang === "pl" ? "success" : "outline"}>
                    {lang.toUpperCase()}
                  </Badge>
                  {option.fallback && <Badge variant="warning">zapas</Badge>}
                  {/* „AI” przed kliknięciem, nie dopiero w odtwarzaczu — to tu się
                      wybiera, a ranking spycha takie kopie na koniec tylko domyślnie. */}
                  {option.machineTranslated && (
                    <Badge variant="warning" title="Napisy z tłumaczenia maszynowego">
                      <BotIcon />
                      napisy AI
                    </Badge>
                  )}
                  {option.rejected !== null && (
                    <Badge
                      variant="destructive"
                      title="Ktoś oznaczył tę kopię jako złą — automat jej nie pobierze"
                    >
                      <ThumbsDownIcon />
                      odrzucona: {REJECTION_REASON_LABELS_PL[option.rejected.reason].toLowerCase()}
                    </Badge>
                  )}

                  <span className="min-w-0 flex-1 truncate text-muted-foreground">
                    {option.playerLabel} · {option.host}
                    {option.quality !== null && ` · ${option.quality}`}
                  </span>
                  {copy !== null && <Badge variant="outline">{copy}</Badge>}

                  {/*
                    Przywrócenie przy samej kopii: odrzucenie działa dla wszystkich,
                    więc pomyłka bez drogi powrotnej zabierałaby kopię całej instancji.
                    „Pobierz” zostaje dostępne i bez przywracania — jawny wybór odrzuconej
                    kopii jest decyzją tego, kto ją widzi z plakietką.
                  */}
                  {option.rejected !== null && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={restoring === option.rejected.id}
                      onClick={() => {
                        if (option.rejected !== null) void restore(option.rejected.id);
                      }}
                    >
                      <Undo2Icon />
                      Przywróć
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant={index === 0 ? "secondary" : "ghost"}
                    onClick={() => {
                      onPick(option);
                      onOpenChange(false);
                    }}
                  >
                    <PlayIcon />
                    {index === 0 ? "Pobierz tę" : "Pobierz"}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Zamknij</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * „kopia 2 z 3” dla wersji, których nie da się odróżnić niczym innym.
 *
 * docchi wystawia dla jednego odcinka po kilka kopii od tej samej grupy na tym
 * samym hoście i nie mówi, czym się różnią. Zmierzone 12.10.2026: z trzech kopii
 * „anime-odcinki” na `vidara.to` dla Kanojo no Tomodachi odc. 1 jedna była
 * odcinkiem INNEJ serii. Bez numeru trzy wiersze wyglądały identycznie, więc
 * nie sposób było zapamiętać, która była zła, i następnym razem ominąć ją.
 */
function copyLabel(items: VersionOption[], index: number): string | null {
  const keyOf = (o: VersionOption) => `${o.sourceId}|${o.host}|${o.playerLabel}`;
  const target = items[index];
  if (target === undefined) return null;
  const same = items.filter((o) => keyOf(o) === keyOf(target));
  if (same.length < 2) return null;
  return `kopia ${same.indexOf(target) + 1} z ${same.length}`;
}
